// move this to a more common place when we know how this needs to be shared

import { inject, Injectable, Provider } from '@angular/core';
import {Observable} from 'rxjs';
import { ApBaseClass } from '../ap-base-class/index';
import { StringKeyOf } from '../types/index';
import { GenericEventSink, GenericEventSinkToken } from './GenericEventSink';

export interface ApGenericEventListener<TEventName, TData> {
  eventName: TEventName;

  // the consumer of this is expected to call this callback with an observable of the events
  // for this eventName
  onEventStreamCreated: (data$: Observable<TData>) => void;
}

export interface ApGenericEvent<TEventName, TData> {
  eventName: TEventName;
  data: TData;
}

// This is the structure of an EventMap (eventName => type of the data associated with the event):
export type AnyApEventMap = {
  [eventName: string]: any;
};
// This is another option for defining ApEventMap which is similar, but slightly different
// (it's more strict, because it doesn't allow accessing any property)
export type ApEventMap = object;

// This is a utility type:
// Given a TEventMap, and keys of that map, it returns a union of all the possible ApGenericEvents
export type ApGenericEventsFromMap<TEventMap extends ApEventMap, TEventMapKeys extends keyof TEventMap> = {
  [K in TEventMapKeys]: ApGenericEvent<K, TEventMap[K]>;
}[TEventMapKeys];

// This interface is for providing for specific typing around events in an EventManager
export interface IEventMapManager<TEventMap extends ApEventMap> {
  fireNamedEvent<TEventName extends StringKeyOf<TEventMap>>(
    managerId: string,
    eventName: TEventName,
    data: TEventMap[TEventName],
  ): void;

  registerNamedEvent<TEventName extends StringKeyOf<TEventMap>>(
    managerId: string,
    eventName: TEventName,
  ): Observable<ApGenericEvent<TEventName, TEventMap[TEventName]>>;
}



// TODO: inject this everywhere instead of BridgeService
@Injectable({
  providedIn: 'root',
})
export class GenericEventManager<TEventMap extends ApEventMap = never>
extends ApBaseClass {
  protected readonly genericEventSink: GenericEventSink = inject(GenericEventSinkToken);

  static formatSpecificNamedEvent(managerId: string, eventName: string): string {
    const specificEventName = managerId + '-' + eventName;
    return specificEventName;
  }

  // shortcut to provide a subclass of GenericEventManager as itself, and as GenericEventManager
  static provideAsSelfAndBase(): Provider[] {
    return [
      this,
      this.provideAsBase(),
    ];
  }

  static provideAsBase(): Provider {
    return {
      provide: GenericEventManager,
      useExisting: this,
    };
  }

  fireEvent<TEventName extends StringKeyOf<TEventMap> = never>(
    eventName: TEventName,
    data: TEventMap[TEventName],
  ): void {
    this.genericEventSink.fireEvent(eventName, data);
  }

  on<TEventName extends StringKeyOf<TEventMap> = never>(
    eventName: TEventName,
  ): Observable<TEventMap[TEventName]> {
    return this.genericEventSink.on<TEventMap[TEventName]>(eventName);
  }

  once<TEventName extends StringKeyOf<TEventMap> = never>(
    eventName: TEventName,
  ): Observable<TEventMap[TEventName]> {
    return this.genericEventSink.once<TEventMap[TEventName]>(eventName);
  }
}
